import React from 'react';
import {
    Edit,
    SimpleForm,
    TextInput,
    SelectInput,
    BooleanInput,
    DateTimeInput,
    required,
    useTranslate,
    RadioButtonGroupInput,
    SelectArrayInput,
    Toolbar,
    SaveButton,
    useRedirect,
    useRecordContext,
    useNotify,
    FormDataConsumer,
} from 'react-admin';
import { useFormContext } from 'react-hook-form';
import {
    Box,
    Alert,
    Typography,
    Card,
    CardContent,
    IconButton,
    Chip,
    FormControlLabel,
    Checkbox,
    Divider
} from '@mui/material';
import {
    Message,
    Settings,
    People,
    Schedule,
    Notifications,
    ArrowBack,
    Info,
    CheckCircle,
    Send,
    AccessTime
} from '@mui/icons-material';
import ImageUploadField from './ImageUploadField';
import ActionTypeSelector from './ActionTypeSelector';
import { MessageMultiLanguageFields } from './MessageMultiLanguageFields';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const MESSAGE_KNOWN_FIELDS = new Set([
    'title', 'subtitle', 'content', 'content_html', 'type',
    'image_url', 'icon_name', 'tags', 'action_type',
    'action_target', 'action_label', 'target_type',
    'target_membership_levels', 'target_user_ids', 'target_data',
    'priority', 'status', 'valid_from', 'valid_until',
    'send_push', 'send_email', 'push_title', 'push_body',
    'push_subtitle', 'push_sound', 'push_badge_increment',
    'push_navigation_screen', 'push_navigation_params',
    'push_category', 'is_active', 'translations',
    'scheduled_at', 'expires_at',
]);

const mapMessageBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const messageFieldLabelKey = (field: string): string =>
    MESSAGE_KNOWN_FIELDS.has(field)
        ? `resources.messages.fields.${field}`
        : field;

const EditToolbar = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { setValue, watch } = useFormContext();

    // Watch the resend_notification field from the form (not part of the database record)
    // Using 'as any' since this is a UI-only field not in the record type
    const resendNotification = watch('resend_notification' as any) || false;

    // Check if message was already sent
    const wasSent = record?.total_recipients > 0;

    const handleResendChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setValue('resend_notification' as any, e.target.checked);
    };

    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            {wasSent && (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={resendNotification}
                            onChange={handleResendChange}
                            color="warning"
                        />
                    }
                    label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Send fontSize="small" />
                            <Typography variant="body2">
                                {translate('resources.messages.actions.resendNotification')}
                            </Typography>
                        </Box>
                    }
                />
            )}
            <Box sx={{ flex: 1 }} />
            <SaveButton
                variant="contained"
                sx={{
                    backgroundColor: '#00C853',
                    '&:hover': {
                        backgroundColor: '#00A843',
                        boxShadow: '0 4px 12px rgba(0,200,83,0.3)',
                    },
                }}
            />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    const record = useRecordContext();

    // Determine status
    let status = 'draft';
    const now = new Date();
    const validFrom = record?.valid_from ? new Date(record.valid_from) : null;

    if (record?.total_recipients > 0) {
        status = 'sent';
    } else if (validFrom && validFrom > now) {
        status = 'scheduled';
    }

    const statusConfig = {
        sent: { color: '#4CAF50', icon: <CheckCircle />, label: translate('resources.messages.statuses.sent') },
        scheduled: { color: '#2196F3', icon: <AccessTime />, label: translate('resources.messages.statuses.scheduled') },
        draft: { color: '#9E9E9E', icon: <Info />, label: translate('resources.messages.statuses.draft') }
    };

    const config = statusConfig[status as keyof typeof statusConfig];

    return (
        <Box sx={{ mb: 4 }}>
            <Box sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                mb: 2,
                flexWrap: 'wrap',
            }}>
                <IconButton
                    onClick={() => redirect('/messages')}
                    size="large"
                    sx={{
                        backgroundColor: '#FFFFFF',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                        '&:hover': {
                            backgroundColor: '#F5F5F5',
                            boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                        },
                    }}
                    title={translate('resources.messages.create.backToList')}
                >
                    <ArrowBack />
                </IconButton>
                <Box sx={{ flex: 1 }}>
                    <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' }, wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
                        {translate('ra.action.edit')} {translate('resources.messages.name', { smart_count: 1 })}
                    </Typography>
                    <Typography variant="body1" color="text.secondary">
                        {record?.title || translate('resources.messages.fields.title')}
                    </Typography>
                </Box>
                <Chip
                    icon={config.icon}
                    label={config.label}
                    sx={{
                        backgroundColor: `${config.color}20`,
                        color: config.color,
                        fontWeight: 600,
                        '& .MuiChip-icon': {
                            color: config.color
                        }
                    }}
                />
            </Box>
        </Box>
    );
};

// Wrapper components for form fields
const ImageUploadWrapper = ({ source }: { source: string }) => {
    const { setValue, watch } = useFormContext();
    const translate = useTranslate();
    const value = watch(source);

    return (
        <ImageUploadField
            source={source}
            value={value}
            onChange={(newValue) => setValue(source, newValue, { shouldDirty: true, shouldValidate: true })}
            label={translate('resources.messages.fields.image')}
            helperText={translate('resources.messages.fields.imageHelp')}
        />
    );
};

const ActionTypeSelectorWrapper = () => {
    const { setValue, watch, formState } = useFormContext();
    const actionType = watch('action_type') || 'none';
    const actionTarget = watch('action_target');

    return (
        <ActionTypeSelector
            actionType={actionType}
            actionTarget={actionTarget}
            onActionTypeChange={(value) => {
                setValue('action_type', value, { shouldDirty: true, shouldValidate: true });
            }}
            onActionTargetChange={(value) => {
                setValue('action_target', value, { shouldDirty: true, shouldValidate: true });
            }}
        />
    );
};

const MessageEdit = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});

    return (
        <Edit
            title={false}
            actions={false}
            component="div"
            mutationMode="pessimistic"
            mutationOptions={{
                onSuccess: (data: any) => {
                    setServerErrors({});
                    if (data?.notifications_resent) {
                        notify('resources.messages.notifications.notificationsResent', { type: 'success' });
                    } else {
                        notify('resources.messages.notifications.updateSuccess', { type: 'success' });
                    }
                    redirect('/messages');
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapMessageBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(messageFieldLabelKey(field), { _: field });
                            summaryLines.push(`\u2022 ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaEdit-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<EditToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', '& .MuiFormControl-root': { width: '100%' }, pb: { xs: 10, sm: 2 } }}>
                    <PageHeader />

                    <MessageStatistics />
                    <MessageAlerts />

                    {/* Message Content Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Message sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.sections.content')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="title"
                                    label={translate('resources.messages.fields.title')}
                                    validate={[required()]}
                                    fullWidth
                                />

                                <TextInput
                                    source="content"
                                    label={translate('resources.messages.fields.content')}
                                    multiline
                                    rows={4}
                                    validate={[required()]}
                                    fullWidth
                                    helperText={translate('resources.messages.fields.contentHelp')}
                                />

                                <ImageUploadWrapper source="image_url" />
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Message Properties */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Settings sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                    {translate('resources.messages.create.propertiesSection')}
                                </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', md: 'row' } }}>
                                <Box sx={{ flex: 1, '& .MuiFormControl-root': { width: '100%' } }}>
                                    <SelectInput
                                        source="type"
                                        label={translate('resources.messages.fields.type')}
                                        choices={[
                                            { id: 'announcement', name: translate('resources.messages.types.announcement') },
                                            { id: 'promotion', name: translate('resources.messages.types.promotion') },
                                            { id: 'system', name: translate('resources.messages.types.system') },
                                            { id: 'personal', name: translate('resources.messages.types.personal') },
                                            { id: 'alert', name: translate('resources.messages.types.alert') },
                                        ]}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                </Box>
                                <Box sx={{ flex: 1, '& .MuiFormControl-root': { width: '100%' } }}>
                                    <SelectInput
                                        source="priority"
                                        label={translate('resources.messages.fields.priority')}
                                        choices={[
                                            { id: 'low', name: translate('resources.messages.priorities.low') },
                                            { id: 'normal', name: translate('resources.messages.priorities.normal') },
                                            { id: 'high', name: translate('resources.messages.priorities.high') },
                                            { id: 'urgent', name: translate('resources.messages.priorities.urgent') },
                                        ]}
                                        validate={[required()]}
                                        fullWidth
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MessageMultiLanguageFields formData={formData} />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>

                    {/* Target Audience */}
                    <TargetAudienceSection />

                    {/* Delivery & Actions */}
                    <DeliveryActionsSection />

                    {/* Scheduling */}
                    <SchedulingSection />
                </Box>
            </SimpleForm>
        </Edit>
    );
};

// Additional component sections
const MessageStatistics = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || record.total_recipients === 0) return null;

    return (
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Card sx={{ flex: 1, p: 2, textAlign: 'center', backgroundColor: '#F5F5F5' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#1976D2', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {record.total_recipients}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.messages.fields.totalRecipients')}
                </Typography>
            </Card>
            <Card sx={{ flex: 1, p: 2, textAlign: 'center', backgroundColor: '#F5F5F5' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#4CAF50', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {record.total_reads}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.messages.fields.totalReads')}
                </Typography>
            </Card>
            <Card sx={{ flex: 1, p: 2, textAlign: 'center', backgroundColor: '#F5F5F5' }}>
                <Typography variant="h4" sx={{ fontWeight: 600, color: '#FF9800', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
                    {record.total_recipients > 0
                        ? `${Math.round((record.total_reads / record.total_recipients) * 100)}%`
                        : '0%'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {translate('resources.messages.fields.readRate')}
                </Typography>
            </Card>
        </Box>
    );
};

const MessageAlerts = () => {
    const record = useRecordContext();
    const translate = useTranslate();

    if (!record || record.total_recipients === 0) return null;

    return (
        <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
                {translate('resources.messages.editSentInfo')}
            </Typography>
        </Alert>
    );
};

const TargetAudienceSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { watch } = useFormContext();
    const wasSent = record?.total_recipients > 0;
    const targetType = watch('target_type');

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <People sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.messages.create.targetingSection')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <RadioButtonGroupInput
                        source="target_type"
                        label={translate('resources.messages.fields.targetType')}
                        choices={[
                            { id: 'all', name: translate('resources.messages.targetTypes.all') },
                            { id: 'membership', name: translate('resources.messages.targetTypes.membership') },
                            // Temporarily disabled individual targeting due to API issues
                            // { id: 'individual', name: translate('resources.messages.targetTypes.individual') },
                        ]}
                        validate={[required()]}
                        disabled={wasSent}
                    />

                    {/* Membership Level Selection - only show when membership targeting is selected */}
                    {targetType === 'membership' && (
                        <SelectArrayInput
                            source="target_data.membership_levels"
                            label={translate('resources.messages.fields.membershipLevels')}
                            choices={[
                                { id: 'standard', name: translate('resources.messages.memberships.standard') },
                                { id: 'silver', name: translate('resources.messages.memberships.silver') },
                                { id: 'gold', name: translate('resources.messages.memberships.gold') },
                                { id: 'platinum', name: translate('resources.messages.memberships.platinum') },
                                { id: 'vip', name: translate('resources.messages.memberships.vip') },
                            ]}
                            helperText={translate('resources.messages.fields.membershipHelp')}
                            disabled={wasSent}
                            fullWidth
                        />
                    )}

                    {/* Individual User Selection - only show when individual targeting is selected */}
                    {/* Temporarily disabled
                    {targetType === 'individual' && (
                        <ReferenceArrayInput
                            source="target_data.user_ids"
                            reference="app-users"
                            label={translate('resources.messages.fields.targetUsers')}
                            disabled={wasSent}
                        >
                            <SelectArrayInput optionText="email" />
                        </ReferenceArrayInput>
                    )}
                    */}
                </Box>
            </CardContent>
        </Card>
    );
};

const DeliveryActionsSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const { watch } = useFormContext();
    const wasSent = record?.total_recipients > 0;
    const sendPush = watch('send_push');

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Notifications sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.messages.create.deliverySection')}
                    </Typography>
                </Box>

                <ActionTypeSelectorWrapper />

                <Divider sx={{ my: 2 }} />

                {/* For already sent messages, show the original push setting as read-only info */}
                {wasSent ? (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Typography variant="body2">
                            {record?.send_push
                                ? translate('resources.messages.pushNotificationInfo')
                                : translate('resources.messages.pushNotEnabledMessage')
                            }
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                            {translate('resources.messages.resendPushHelp')}
                        </Typography>
                    </Alert>
                ) : (
                    <Alert severity="info" sx={{ mt: 2 }}>
                        <Box sx={{ mb: 2 }}>
                            <BooleanInput
                                source="send_push"
                                label={translate('resources.messages.fields.sendPush')}
                            />
                        </Box>
                        {sendPush && (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 2 }}>
                                <TextInput
                                    source="push_title"
                                    label={translate('resources.messages.fields.pushTitle', { _: 'Push Title' })}
                                    validate={[required()]}
                                    fullWidth
                                />
                                <TextInput
                                    source="push_body"
                                    label={translate('resources.messages.fields.pushBody', { _: 'Push Body' })}
                                    multiline
                                    rows={2}
                                    validate={[required()]}
                                    fullWidth
                                    helperText={translate('resources.messages.fields.pushBodyHelp', { _: 'Max 150 characters; longer text will be truncated.' })}
                                />
                            </Box>
                        )}
                        <Typography variant="body2">
                            {translate('resources.messages.pushNotificationInfo')}
                        </Typography>
                    </Alert>
                )}
            </CardContent>
        </Card>
    );
};

const SchedulingSection = () => {
    const record = useRecordContext();
    const translate = useTranslate();
    const wasSent = record?.total_recipients > 0;

    if (wasSent) return null;

    return (
        <Card sx={{ mb: 3, overflow: 'hidden' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <Schedule sx={{ mr: 2, color: '#00C853' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        {translate('resources.messages.create.schedulingSection')}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' }, flexDirection: { xs: 'column', sm: 'row' } }}>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                        <DateTimeInput
                            source="valid_from"
                            label={translate('resources.messages.fields.scheduledAt')}
                            helperText={translate('resources.messages.fields.scheduledHelp')}
                            fullWidth
                        />
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                        <DateTimeInput
                            source="valid_until"
                            label={translate('resources.messages.fields.expiresAt')}
                            helperText={translate('resources.messages.fields.expiresHelp')}
                            fullWidth
                        />
                    </Box>
                </Box>
            </CardContent>
        </Card>
    );
};

export default MessageEdit;